"use client";

import { useMemo } from "react";
import { motion } from "framer-motion";

interface CognitivePath {
  id: string;
  label: string;
  round: number;
  content: string;
  confidence: number;
  convergenceScore?: number;
}

interface CognitiveMapGraphProps {
  paths: CognitivePath[];
  primaryColor: string;
  secondaryColor: string;
  onPathSelect?: (pathId: string) => void;
}

/**
 * Visual Cognitive Map Graph
 * 
 * Displays cognitive paths as an interactive network visualization
 * Shows convergence between paths and confidence levels
 */
export default function CognitiveMapGraph({
  paths,
  primaryColor,
  secondaryColor,
  onPathSelect,
}: CognitiveMapGraphProps) {
  // Build graph structure
  const { nodes, edges, convergenceCount } = useMemo(() => {
    const nodes = paths.map((path, idx) => ({
      id: path.id,
      label: `Path ${idx + 1}`,
      title: path.label,
      round: path.round,
      confidence: path.confidence,
      convergenceScore: path.convergenceScore,
    }));

    // Create edges showing convergence
    const edges: Array<{ from: string; to: string; strength: number }> = [];
    let convergenceCount = 0;

    paths.forEach((path1, i) => {
      paths.slice(i + 1).forEach((path2) => {
        // Calculate similarity/convergence
        const similarity = calculatePathSimilarity(path1.content, path2.content);
        if (similarity > 0.6) {
          edges.push({
            from: path1.id,
            to: path2.id,
            strength: similarity,
          });
          if (similarity > 0.8) {
            convergenceCount++;
          }
        }
      });
    });

    return { nodes, edges, convergenceCount };
  }, [paths]);

  // Group nodes by round for layout
  const round1Nodes = nodes.filter(n => n.round === 1);
  const round2Nodes = nodes.filter(n => n.round === 2);
  const round3Nodes = nodes.filter(n => n.round === 3);

  return (
    <div className="w-full rounded-2xl overflow-hidden" style={{
      background: `linear-gradient(135deg, ${primaryColor}08, ${secondaryColor}05)`,
      border: `1px solid ${primaryColor}20`,
      minHeight: "400px",
    }}>
      {/* Convergence indicator */}
      {convergenceCount > 0 && (
        <div className="absolute top-4 right-4 p-2 rounded-lg z-10" style={{
          background: `${primaryColor}20`,
          border: `1px solid ${primaryColor}40`,
        }}>
          <div className="text-xs font-semibold" style={{ color: primaryColor }}>
            {convergenceCount} Convergence{convergenceCount !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Graph visualization using CSS grid */}
      <div className="p-6 relative" style={{ minHeight: "400px" }}>
        {/* Round 1: Initial Analysis (Top) */}
        {round1Nodes.length > 0 && (
          <div className="mb-8">
            <div className="text-xs font-bold uppercase tracking-wide mb-4 opacity-70" style={{ color: primaryColor }}>
              Round 1: Initial Analysis
            </div>
            <div className="flex flex-wrap gap-3">
              {round1Nodes.map((node, idx) => (
                <CognitivePathNode
                  key={node.id}
                  node={node}
                  index={idx}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  onClick={() => onPathSelect?.(node.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Convergence Lines */}
        {edges.length > 0 && (
          <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
            <svg className="w-full h-full">
              {edges.map((edge, idx) => {
                const fromNode = nodes.find(n => n.id === edge.from);
                const toNode = nodes.find(n => n.id === edge.to);
                if (!fromNode || !toNode) return null;
                
                // Calculate positions (simplified - would need actual layout in production)
                const fromY = fromNode.round === 1 ? 80 : fromNode.round === 2 ? 200 : 320;
                const toY = toNode.round === 1 ? 80 : toNode.round === 2 ? 200 : 320;
                const fromX = 50 + (nodes.indexOf(fromNode) % 4) * 150;
                const toX = 50 + (nodes.indexOf(toNode) % 4) * 150;
                
                return (
                  <line
                    key={idx}
                    x1={fromX}
                    y1={fromY}
                    x2={toX}
                    y2={toY}
                    stroke={primaryColor}
                    strokeWidth={edge.strength * 3}
                    opacity={edge.strength * 0.6}
                    strokeDasharray={fromNode.round !== toNode.round ? "5,5" : "0"}
                  />
                );
              })}
            </svg>
          </div>
        )}

        {/* Round 2: Validation (Middle) */}
        {round2Nodes.length > 0 && (
          <div className="mb-8">
            <div className="text-xs font-bold uppercase tracking-wide mb-4 opacity-70" style={{ color: primaryColor }}>
              Round 2: Cross-Validation
            </div>
            <div className="flex flex-wrap gap-3">
              {round2Nodes.map((node, idx) => (
                <CognitivePathNode
                  key={node.id}
                  node={node}
                  index={idx}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  onClick={() => onPathSelect?.(node.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Round 3: Synthesis (Bottom) */}
        {round3Nodes.length > 0 && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wide mb-4 opacity-70" style={{ color: primaryColor }}>
              Round 3: Final Synthesis
            </div>
            <div className="flex flex-wrap gap-3">
              {round3Nodes.map((node, idx) => (
                <CognitivePathNode
                  key={node.id}
                  node={node}
                  index={idx}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  onClick={() => onPathSelect?.(node.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Individual cognitive path node
 */
function CognitivePathNode({
  node,
  index,
  primaryColor,
  secondaryColor,
  onClick,
}: {
  node: { id: string; label: string; confidence: number; convergenceScore?: number };
  index: number;
  primaryColor: string;
  secondaryColor: string;
  onClick: () => void;
}) {
  const size = 20 + (node.confidence * 30);
  const isHighConvergence = node.convergenceScore && node.convergenceScore > 0.7;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1 }}
      onClick={onClick}
      className="cursor-pointer group relative"
    >
      <div
        className="rounded-full flex items-center justify-center transition-all group-hover:scale-110"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          background: isHighConvergence
            ? `linear-gradient(135deg, ${primaryColor}, ${primaryColor}CC)`
            : `linear-gradient(135deg, ${secondaryColor}80, ${secondaryColor}60)`,
          border: `2px solid ${primaryColor}`,
          boxShadow: isHighConvergence
            ? `0 0 20px ${primaryColor}40`
            : `0 0 10px ${primaryColor}20`,
        }}
      >
        <span
          className="text-xs font-bold"
          style={{
            color: isHighConvergence ? "#fff" : primaryColor,
          }}
        >
          {index + 1}
        </span>
      </div>
      
      {/* Confidence indicator */}
      <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
        <div
          className="text-xs px-1 rounded"
          style={{
            background: `${primaryColor}20`,
            color: primaryColor,
          }}
        >
          {Math.round(node.confidence * 100)}%
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Calculate similarity between two path contents
 */
function calculatePathSimilarity(content1: string, content2: string): number {
  // Extract key terms from both contents
  const words1 = new Set(
    content1
      .toLowerCase()
      .replace(/[^\w\s]/g, " ")
      .split(/\s+/)
      .filter(w => w.length > 3)
  );
  const words2 = new Set(
    content2
      .toLowerCase()
      .replace(/[^\w\s]/g, " ")
      .split(/\s+/)
      .filter(w => w.length > 3)
  );
  
  // Calculate Jaccard similarity
  const intersection = new Set([...words1].filter(x => words2.has(x)));
  const union = new Set([...words1, ...words2]);
  
  if (union.size === 0) return 0;
  return intersection.size / union.size;
}
